import React from "react";
import { XAxis, YAxis } from "../hooks/useLineChartFormState";
import { Input, Select, TextArea, FilterPanel } from "@aidkitorg/component-library";
import { useLocalizedStrings } from "../../../Localization";
import { useFieldOptions } from "../../hooks/useFieldOptions";
import { ReportFilterConfig } from "@aidkitorg/types/lib/admin-panel/filters";
import { useSettingsFilterFields } from "../../Filters/hooks/useSettingsFilterFields";
import { useFieldValueSuggestions } from "../../shared/hooks/useFieldValueSuggestions";

export type LineChartFormFieldsProps = {
  title: string,
  setTitle: (val: string) => void,

  description: string,
  setDescription: (val: string) => void,

  xAxis: XAxis,
  setXAxis: (val: XAxis) => void,

  yAxis: YAxis,
  setYAxis: (val: YAxis) => void,

  filterConfig?: ReportFilterConfig,
  setFilterConfig: (val: ReportFilterConfig | undefined) => void,
}

export const LineChartFormFields: React.FC<LineChartFormFieldsProps> = ({
  title, setTitle,
  description, setDescription,
  xAxis, setXAxis,
  yAxis, setYAxis,
  filterConfig,
  setFilterConfig,
}) => {
  const L = useLocalizedStrings()
  const { fieldOptions, getSelectedFieldOption } = useFieldOptions({
    componentKinds: ['SubmitButton']
  })
  const availableFieldsForFilters = useSettingsFilterFields()
  const { fetchSuggestions } = useFieldValueSuggestions()

  const bucketOptions = [
    { label: L.admin_panel.dashboards.bucket_minute, value: 'minute' },
    { label: L.admin_panel.dashboards.bucket_hour, value: 'hour' },
    { label: L.admin_panel.dashboards.bucket_day, value: 'day' },
    { label: L.admin_panel.dashboards.bucket_week, value: 'week' },
    { label: L.admin_panel.dashboards.bucket_month, value: 'month' },
    { label: L.admin_panel.dashboards.bucket_year, value: 'year' },
  ]

  const getSelectedBucket = (bucket: string) => {
    return bucketOptions.find(opt => opt.value === bucket) || bucketOptions[2]
  }

  return <>
    <div className="w-[300px] flex flex-col gap-y-2">

      <Input
        required
        label={L.admin_panel.dashboards.chart_title}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <Input
        required
        placeholder={L.admin_panel.dashboards.x_axis_title_placeholder}
        label={L.admin_panel.dashboards.x_axis_title}
        value={xAxis.title}
        onChange={(e) => setXAxis({...xAxis, title: e.target.value})}
      />

      <Select
        portal
        hideLabel
        virtualized
        options={fieldOptions}
        label={L.admin_panel.dashboards.date_field}
        description={L.admin_panel.dashboards.date_field_description}
        labelLocation="above"
        value={getSelectedFieldOption(xAxis.dataField)}
        onChange={(value) => {
          if (value) setXAxis({...xAxis, dataField: value.value})
        }}
        placeholder={L.admin_panel.dashboards.select_date_field_placeholder}
        searchable
      />

      <Select
        portal
        options={bucketOptions}
        label={L.admin_panel.dashboards.group_by}
        description={L.admin_panel.dashboards.time_bucket_description}
        labelLocation="above"
        value={getSelectedBucket(xAxis.bucket)}
        onChange={(value) => {
          if (value) setXAxis({...xAxis, bucket: value.value as "minute" | "day" | "hour" | "year" | "month"})
        }}
      />

      <Input
        required
        label={L.admin_panel.dashboards.y_axis_title}
        placeholder={L.admin_panel.dashboards.y_axis_title_placeholder}
        value={yAxis.title}
        onChange={(e) => setYAxis({...yAxis, title: e.target.value})}
      />
    </div>

    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {L.admin_panel.dashboards.y_axis_values}
      </label>
      <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md border">
        📊 <strong>{L.admin_panel.dashboards.applicant_count}</strong> - {L.admin_panel.dashboards.applicant_count_line_chart_desc}
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {L.admin_panel.dashboards.filters_label}
      </label>
      <FilterPanel
        value={filterConfig}
        onChange={setFilterConfig}
        availableFields={availableFieldsForFilters}
        instanceId="line-chart-form-filters"
        fetchFieldValues={fetchSuggestions}
        inline
      />
    </div>

    <TextArea
      label={L.admin_panel.dashboards.description}
      showLabel
      value={description}
      onChange={(e) => setDescription(e.target.value)}
    />
  </>
}